<template>
  <footer class="market-ticker" aria-label="底部指数行情">
    <div class="ticker-quotes" :aria-busy="loading" :title="error || '第三方指数行情，可能存在延迟'">
      <div v-for="item in indexes" :key="item.code" class="ticker-index" :title="sharedList ? '与股指列表同步的行情' : item.time ? `行情时间：${item.time}` : item.price != null ? '简版指数行情，接口不提供行情时间' : '行情暂未更新'">
        <span>{{ item.name }}</span>
        <b :class="color(item.percent)">{{ format(item.price) }}</b>
        <b :class="color(item.percent)">{{ format(item.change) }}</b>
        <b :class="color(item.percent)">{{ format(item.percent, '%') }}</b>
      </div>
    </div>
    <div class="ticker-status" title="按服务器校准时间、市场时区、常规交易时段及周末判断；特殊休市以交易所公告为准，平台下单权限由服务端独立校验。">
      <time>{{ clock }}</time><span>{{ marketName }} · {{ session }}</span>
      <button v-if="error" type="button" :disabled="loading" @click="load">行情重试</button>
    </div>
  </footer>
</template>

<script>
import { quoteDisplay, quoteNumber } from '@/utils/quoteDisplay'
import { indexMarketFeed, indexQuoteCode } from '@/utils/indexQuotes'
import { serverNow } from '@/utils/marketClock'
import { pollMarket } from '@/utils/marketRefresh'
import { marketEnabled } from '@/utils/systemConfig'
import { footerMarket, marketIndexes, getMarketIndexQuotes, marketSession, indexQuotes } from './marketTicker'

export default {
  name: 'MarketTicker',
  data: () => ({ quotes: {}, now: serverNow(), error: '', loading: false, requestId: 0, clockTimer: null }),
  computed: {
    market() {
      // Index selection is independent of the HK/US individual-stock service switches.
      if (indexMarketFeed.active && ['cn', 'hk', 'us'].includes(indexMarketFeed.market)) return indexMarketFeed.market
      const market = footerMarket(this.$route)
      return marketEnabled(market) ? market : 'cn'
    },
    marketName() { return { cn: 'A股', hk: '港股', us: '美股' }[this.market] },
    sharedList() { return indexMarketFeed.active },
    indexes() {
      if (!this.sharedList) return indexQuotes(this.market, this.quotes)
      return marketIndexes[this.market].map(([code, name]) => {
        const row = indexMarketFeed.rows.find(item => indexQuoteCode(item.full_code) === code) || {}
        return { code, name, price: quoteNumber(row.last_px), change: quoteNumber(row.px_change), percent: quoteNumber(row.px_change_rate), time: null }
      })
    },
    clock() { return this.now.toLocaleString('sv-SE', { timeZone: 'Asia/Shanghai' }).replace(/-/g, '.') },
    session() { return marketSession(this.market, this.now) }
  },
  watch: {
    market: { immediate: true, handler() { this.quotes = {}; return this.load() } },
    sharedList(active) { this.requestId++; this.loading = false; this.error = ''; if (!active) return this.load() }
  },
  mounted() {
    this.stopRefresh = pollMarket(() => { if (!this.sharedList && !this.loading) return this.load() }, 15000, () => this.market)
    this.clockTimer = setInterval(() => { this.now = serverNow() }, 1000)
  },
  beforeDestroy() { if (this.stopRefresh) this.stopRefresh(); clearInterval(this.clockTimer); this.requestId++ },
  methods: {
    async load() {
      if (this.sharedList) return
      const market = this.market
      const request = ++this.requestId
      this.loading = true
      try {
        const quotes = await getMarketIndexQuotes(market)
        if (request !== this.requestId || this.sharedList) return
        const valid = indexQuotes(market, quotes).filter(item => item.price > 0 && item.change != null && item.percent != null)
        if (!valid.length) throw new Error('暂无指数行情')
        this.quotes = { ...this.quotes, ...Object.fromEntries(valid.map(item => [item.code, quotes[item.code]])) }
        this.error = valid.length < 3 ? '部分指数暂未更新，保留最近行情' : ''
      } catch (_) {
        if (request === this.requestId) this.error = '指数行情暂时无法更新，保留最近行情'
      } finally { if (request === this.requestId) this.loading = false }
    },
    color(value) { return value > 0 ? 'rise' : value < 0 ? 'fall' : '' },
    format(value, suffix = '') { return quoteDisplay(value, suffix, 1, 2) }
  }
}
</script>

<style scoped>
.market-ticker { display: flex; align-items: center; justify-content: space-between; gap: 18px; flex: 0 0 36px; min-height: 36px; min-width: 0; padding: 0 14px; border-top: 1px solid var(--theme-border); background: var(--theme-panel); color: var(--theme-label); font-size: 13px; line-height: 1.4; }
.ticker-quotes { display: flex; gap: 24px; min-width: 0; overflow-x: auto; white-space: nowrap; scrollbar-width: thin; }
.ticker-index { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }
.ticker-index b { font-weight: normal; font-variant-numeric: tabular-nums; }
.ticker-status { display: flex; gap: 12px; flex-shrink: 0; white-space: nowrap; color: var(--theme-muted); }
.ticker-status button { padding: 0; border: 0; background: transparent; color: var(--theme-accent); cursor: pointer; }
.rise { color: var(--quote-up); } .fall { color: var(--quote-down); }
@media (max-width: 1200px) { .ticker-status time { display: none; } .ticker-quotes { gap: 18px; } }
</style>
